import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import GlimpseCard from 'components/card/card.component'
import GlimpseTable from 'components/table/table.component'
import {
  getChatSessions,
  deleteChatSession,
  getChatHistory,
} from 'services/chat.service'
import { ChatSession } from 'interfaces/chat.interface'
import { displayError, displaySuccess } from 'components/Toast/toast.component'
import GlimpsePopup from 'components/popup/popup.component'
import Loading from 'components/loading/loading.component'
import { useSelector } from 'react-redux'
import Button from 'components/button/button'

const AdminChatsScreen: React.FC = () => {
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(
    null
  )
  const [showSessionDetails, setShowSessionDetails] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [sessionFilter, setSessionFilter] = useState<'all' | 'my'>('all')
  const queryClient = useQueryClient()
  const columnHelper = createColumnHelper<ChatSession>()
  const [isExporting, setIsExporting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 25

  const { userInfo } = useSelector((state: any) => state.auth)
  const userEmail = userInfo?.user.email
  const isAdmin = userInfo?.user.role === 'super'

  // Fetch all chat sessions
  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ['admin-chat-sessions', currentPage, pageSize, searchTerm],
    queryFn: () => getChatSessions(),
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Fetch session details when viewing
  const { data: sessionHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['admin-chat-history', selectedSession?.session_id],
    queryFn: () =>
      selectedSession ? getChatHistory(selectedSession.session_id) : null,
    enabled: !!selectedSession,
    refetchOnWindowFocus: false,
    retry: 0,
  })

  // Delete session mutation
  const deleteSessionMutation = useMutation({
    mutationFn: deleteChatSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-chat-sessions'] })
      displaySuccess('Chat session deleted successfully')
    },
    onError: () => {
      displayError('Failed to delete chat session')
    },
  })

  const handleRowClick = (session: ChatSession) => {
    setSelectedSession(session)
    setShowSessionDetails(true)
  }

  const handleDeleteSession = (sessionId: string) => {
    if (window.confirm('Are you sure you want to delete this chat session?')) {
      deleteSessionMutation.mutate(sessionId)
    }
  }

  const handleExportSession = (session: ChatSession) => {
    if (!sessionHistory) return
    setIsExporting(true)

    const exportData = {
      session_info: sessionHistory.session_info,
      messages: sessionHistory.messages,
      export_date: new Date().toISOString(),
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `chat-session-${session.session_id}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    setIsExporting(false)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatMessages = (messages: any[]) => {
    const formattedMessages: Array<{
      type: 'user' | 'ai'
      message: any
    }> = []

    messages.forEach(msg => {
      if (msg.role === 'user') {
        formattedMessages.push({
          type: 'user',
          message: msg,
        })
      } else if (msg.role === 'assistant') {
        formattedMessages.push({
          type: 'ai',
          message: msg,
        })
      }
    })

    return formattedMessages
  }

  // Filter sessions based on search term and session filter
  const filteredSessions =
    sessionsData?.sessions?.filter(session => {
      // Apply search term filter
      const matchesSearch =
        session.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.session_id.toLowerCase().includes(searchTerm.toLowerCase())

      // Apply session filter (my sessions vs all sessions)
      const matchesSessionFilter =
        sessionFilter === 'all' ||
        (sessionFilter === 'my' && session.user_email === userEmail)

      // Hide ghost sessions (no user reply, only the seeded welcome) from
      // end-user view. Admins still see everything.
      const isGhost = !isAdmin && (session.message_count ?? 0) <= 1

      return matchesSearch && matchesSessionFilter && !isGhost
    }) || []

  const totalPages = Math.ceil(filteredSessions.length / pageSize)
  const paginatedSessions = filteredSessions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  const tableColumns = [
    ...(isAdmin
      ? [
          columnHelper.accessor('session_id', {
            header: () => 'Session ID',
            cell: info => (
              <span className="font-mono text-xs text-gray-600">
                {info.getValue().slice(0, 8)}...
              </span>
            ),
          }),
        ]
      : []),
    columnHelper.accessor('topic', {
      header: () => 'Topic',
      cell: info => (
        <span className="font-medium">{info.getValue() || 'General Chat'}</span>
      ),
    }),
    ...(isAdmin
      ? [
          columnHelper.accessor('user_email', {
            header: () => 'User',
            cell: info => {
              const userEmail = info.getValue()
              return <span className="text-sm">{userEmail || 'Unknown'}</span>
            },
          }),
        ]
      : []),
    columnHelper.accessor('message_count', {
      header: () => 'Messages',
      cell: info => <span className="text-center">{info.getValue()}</span>,
    }),
    columnHelper.accessor('created_at', {
      header: () => 'Created',
      cell: info => (
        <span className="text-sm text-gray-600">
          {formatDate(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor('updated_at', {
      header: () => 'Last Updated',
      cell: info => (
        <span className="text-sm text-gray-600">
          {formatDate(info.getValue())}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => 'Actions',
      cell: info => {
        const canDelete = info.row.original.user_email === userEmail
        return (
          <div className="flex gap-1">
            <button
              onClick={e => {
                e.stopPropagation()
                handleDeleteSession(info.row.original.session_id)
              }}
              className={`p-1.5 text-red rounded-full ${
                !canDelete
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-red hover:text-white'
              }`}
              title="Delete chat session"
              disabled={!canDelete}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        )
      },
    }),
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center">
          {isAdmin && (
            <div className="flex bg-[#F3F4F6] rounded-lg p-1">
              <button
                onClick={() => setSessionFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  sessionFilter === 'all'
                    ? 'bg-[#FFFFFF] text-[#111827] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#111827]'
                }`}
              >
                All sessions
              </button>
              <button
                onClick={() => setSessionFilter('my')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  sessionFilter === 'my'
                    ? 'bg-[#FFFFFF] text-[#111827] shadow-sm'
                    : 'text-[#6B7280] hover:text-[#111827]'
                }`}
              >
                My sessions
              </button>
            </div>
          )}
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-[#D1D5DB] text-[#111827] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent"
          />
        </div>
      </div>

      <GlimpseCard
        title={
          isAdmin
            ? sessionFilter === 'all'
              ? 'All Chat Sessions'
              : 'My Chat Sessions'
            : 'Chat History'
        }
        titleClass="text-lg"
        subtitle={`Showing: ${filteredSessions.length} of ${
          sessionsData?.total_count || 0
        } total sessions`}
        subtitleClass="text-sm text-darkGray mt-1 font-semibold"
        childrenClass="h-full"
      >
        <GlimpseTable
          columns={tableColumns}
          data={paginatedSessions}
          loadingData={isLoading}
          totalData={filteredSessions.length}
          currentPage={currentPage}
          totalPage={totalPages}
          enableFilter={false}
          enableExport={false}
          enableSearch={false}
          enablePagination={true}
          preventClickOn={['actions']}
          rowSelected={row => handleRowClick(row as ChatSession)}
          onSearch={() => {}}
          filterChanged={() => {}}
          previousPage={() => setCurrentPage(p => Math.max(1, p - 1))}
          nextPage={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
        />
      </GlimpseCard>

      {/* Session Details Modal */}
      {showSessionDetails && selectedSession && (
        <GlimpsePopup
          popup={showSessionDetails}
          onClose={() => {
            setShowSessionDetails(false)
            setSelectedSession(null)
          }}
          title="Chat Session Details"
          showCross={true}
          customClass="max-w-4xl"
        >
          <div className="max-h-96 overflow-y-auto">
            {isHistoryLoading ? (
              <Loading />
            ) : sessionHistory ? (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 border-b-2 border-lightGray">
                  <h3 className="font-bold mb-2 text-lg">
                    Session Information
                  </h3>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div>
                      <span className="font-semibold text-darkGray">
                        Session ID:
                      </span>
                      <span className="ml-2 font-mono text-xs">
                        {selectedSession.session_id}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-darkGray">
                        Title:
                      </span>
                      <span className="ml-2">{selectedSession.topic}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-darkGray">User:</span>
                      <span className="ml-2">
                        {selectedSession.user_email || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-darkGray">
                        Created:
                      </span>
                      <span className="ml-2">
                        {formatDate(selectedSession.created_at)}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-darkGray">
                        Messages:
                      </span>
                      <span className="ml-2">
                        {selectedSession.message_count}
                      </span>
                    </div>
                  </div>
                </div>

                {selectedSession.user_email === userEmail && (
                  <div className="space-y-4 p-4">
                    <div className="flex justify-between items-center">
                      <h3 className="font-bold text-lg">Chat History</h3>
                      <Button
                        onClick={e => {
                          e.stopPropagation()
                          handleExportSession(selectedSession)
                        }}
                        variant="primary"
                        title="Export chat session"
                      >
                        {isExporting ? (
                          <Loading />
                        ) : (
                          <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        )}
                        Export chat
                      </Button>
                    </div>
                    {formatMessages(sessionHistory.messages).map(
                      (item, index) => (
                        <div key={index} className="border-l-4 pl-4">
                          {item.type === 'user' ? (
                            <div className="bg-blue-50 p-3 rounded-md">
                              <div className="text-md font-bold text-black mb-1">
                                User:
                              </div>
                              <div className="text-sm whitespace-pre-wrap">
                                {item.message.content}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(
                                  item.message.timestamp
                                ).toLocaleString()}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gray-50 p-3 rounded-md">
                              <div className="text-md font-bold text-black mb-1">
                                Career Sherpa:
                                {item.message.context &&
                                  item.message.context.relevant_chunks &&
                                  item.message.context.relevant_chunks > 0 && (
                                    <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      RAG Used (
                                      {item.message.context.relevant_chunks}{' '}
                                      chunks)
                                    </span>
                                  )}
                              </div>
                              <div className="text-sm whitespace-pre-wrap">
                                {item.message.content}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(
                                  item.message.timestamp
                                ).toLocaleString()}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No chat history found for this session.
              </div>
            )}
          </div>
        </GlimpsePopup>
      )}
    </div>
  )
}

export default AdminChatsScreen
